import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface DashboardStats {
  totalPacientes: number;
  consultasHoje: number;
  consultasPendentes: number;
}

interface ConsultaRecente {
  id: number;
  pacienteNome: string;
  tipoAtendimento?: string;
  dataAtendimento: string;
}

interface ProximoAtendimento {
  id: number;
  pacienteNome: string;
  tipoAtendimento?: string;
  dataAgendamento: string;
  horaInicio: string;
}

const Dashboard = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalPacientes: 0,
    consultasHoje: 0,
    consultasPendentes: 0,
  });
  const [consultasRecentes, setConsultasRecentes] = useState<ConsultaRecente[]>([]);
  const [proximosAtendimentos, setProximosAtendimentos] = useState<ProximoAtendimento[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarDadosDashboard();
  }, []);

  const carregarDadosDashboard = async () => {
    try {
      setLoading(true);

      // Buscar total de pacientes cadastrados
      const pacientesResponse = await api.get("/api/paciente?page=1&limit=1");
      const totalPacientes = pacientesResponse?.result?.pagination?.total || 0;

      // Buscar tipos de atendimento para identificar consultas
      const tiposAtendimentoResponse = await api.get("/api/tipo_atendimento");
      const tiposAtendimento = tiposAtendimentoResponse?.result || [];
      
      // Filtrar tipos de atendimento que são consultas (categoria CONSULTA ou nome contém "consulta")
      const tiposConsultaIds = tiposAtendimento
        .filter((tipo: any) => 
          tipo.categoria === "CONSULTA" || 
          (tipo.nome && tipo.nome.toLowerCase().includes("consulta"))
        )
        .map((tipo: any) => tipo.id);

      // Buscar agenda de hoje
      const hoje = format(new Date(), "yyyy-MM-dd");
      const agendaResponse = await api.get("/api/agenda");
      const agendaHoje = (agendaResponse?.result || []).filter((item: any) => {
        if (!item.data_agendamento) return false;
        const dataAgendamento = item.data_agendamento?.split("T")[0] || item.data_agendamento;
        const isHoje = dataAgendamento === hoje;
        // Se não há tipo de atendimento definido ou se é um tipo de consulta
        const isConsulta = !item.id_tipo_atendimento || tiposConsultaIds.includes(item.id_tipo_atendimento);
        return isHoje && isConsulta;
      });

      const consultasRealizadas = agendaHoje.filter(
        (item: any) => item.status_agendamento === "CONCLUIDO"
      ).length;

      const consultasPendentes = agendaHoje.filter(
        (item: any) => 
          item.status_agendamento === "AGENDADO" || 
          item.status_agendamento === "CONFIRMADO" ||
          item.status_agendamento === "AGUARDANDO_CONFIRMACAO"
      ).length;

      // Buscar últimos 4 atendimentos realizados
      const atendimentosResponse = await api.get("/api/atendimentos?status=CONCLUIDO&limit=4&page=1");
      const atendimentosRecentes = (atendimentosResponse?.result?.data || []).slice(0, 4).map((atendimento: any) => ({
        id: atendimento.id,
        pacienteNome: atendimento.paciente?.pessoa?.nome || "Paciente não identificado",
        tipoAtendimento: atendimento.tipoAtendimento?.nome || "Sem tipo",
        dataAtendimento: atendimento.data_atendimento,
      }));

      // Buscar próximos atendimentos da agenda (próximos 4)
      const hojeDate = new Date();
      hojeDate.setHours(0, 0, 0, 0);
      
      const todosAgendamentos = (agendaResponse?.result || []).filter((item: any) => {
        const dataAgendamento = new Date(item.data_agendamento?.split("T")[0] || item.data_agendamento);
        dataAgendamento.setHours(0, 0, 0, 0);
        return dataAgendamento >= hojeDate && 
               (item.status_agendamento === "AGENDADO" || 
                item.status_agendamento === "CONFIRMADO" ||
                item.status_agendamento === "AGUARDANDO_CONFIRMACAO");
      });

      // Ordenar por data e hora
      todosAgendamentos.sort((a: any, b: any) => {
        const dataA = new Date(`${a.data_agendamento?.split("T")[0]} ${a.hora_inicio}`);
        const dataB = new Date(`${b.data_agendamento?.split("T")[0]} ${b.hora_inicio}`);
        return dataA.getTime() - dataB.getTime();
      });

      const proximos = todosAgendamentos.slice(0, 4).map((item: any) => ({
        id: item.id,
        pacienteNome: item.paciente?.pessoa?.nome || "Paciente não identificado",
        tipoAtendimento: item.tipoAtendimento?.nome || "Sem tipo",
        dataAgendamento: item.data_agendamento,
        horaInicio: item.hora_inicio,
      }));

      setStats({
        totalPacientes,
        consultasHoje: consultasRealizadas,
        consultasPendentes,
      });
      setConsultasRecentes(atendimentosRecentes);
      setProximosAtendimentos(proximos);
    } catch (error) {
      console.error("Erro ao carregar dados do dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatarDataHora = (data: string, hora?: string) => {
    try {
      const dataObj = new Date(data);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const dataObjSemHora = new Date(dataObj);
      dataObjSemHora.setHours(0, 0, 0, 0);

      if (dataObjSemHora.getTime() === hoje.getTime()) {
        return hora ? `Hoje, ${hora}` : "Hoje";
      }

      const amanha = new Date(hoje);
      amanha.setDate(amanha.getDate() + 1);
      if (dataObjSemHora.getTime() === amanha.getTime()) {
        return hora ? `Amanhã, ${hora}` : "Amanhã";
      }

      return format(dataObj, "dd/MM/yyyy", { locale: ptBR }) + (hora ? `, ${hora}` : "");
    } catch {
      return data + (hora ? `, ${hora}` : "");
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-foreground">Dashboard</h2>
          <p className="text-muted-foreground mt-1">Bem-vindo ao sistema de gestão oftalmológica</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Pacientes Cadastrados */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pacientes Cadastrados
              </CardTitle>
              <Users className="h-5 w-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">
                {loading ? "..." : stats.totalPacientes.toLocaleString("pt-BR")}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Total de pacientes</p>
            </CardContent>
          </Card>

          {/* Consultas Hoje */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Consultas Hoje
              </CardTitle>
              <Calendar className="h-5 w-5 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">
                {loading ? "..." : stats.consultasHoje}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.consultasPendentes} pendentes
              </p>
            </CardContent>
          </Card>

          {/* Exames Realizados - COMENTADO */}
          {/* <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Exames Realizados
              </CardTitle>
              <FileText className="h-5 w-5 text-info" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">156</div>
              <p className="text-xs text-muted-foreground mt-1">Este mês</p>
            </CardContent>
          </Card> */}

          {/* Taxa de Retorno - COMENTADO */}
          {/* <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Taxa de Retorno
              </CardTitle>
              <Activity className="h-5 w-5 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">87%</div>
              <p className="text-xs text-muted-foreground mt-1">+5% vs mês anterior</p>
            </CardContent>
          </Card> */}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Consultas Recentes */}
          <Card>
            <CardHeader>
              <CardTitle>Consultas Recentes</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-4 text-muted-foreground">Carregando...</div>
              ) : consultasRecentes.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  Nenhuma consulta realizada recentemente
                </div>
              ) : (
                <div className="space-y-4">
                  {consultasRecentes.map((consulta) => (
                    <div
                      key={consulta.id}
                      className="flex items-center justify-between border-b pb-3 last:border-0"
                    >
                      <div>
                        <p className="font-medium text-foreground">{consulta.pacienteNome}</p>
                        <p className="text-sm text-muted-foreground">
                          {consulta.tipoAtendimento}
                        </p>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {formatarDataHora(consulta.dataAtendimento)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Próximos Atendimentos */}
          <Card>
            <CardHeader>
              <CardTitle>Próximos Atendimentos</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-4 text-muted-foreground">Carregando...</div>
              ) : proximosAtendimentos.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  Nenhum atendimento agendado
                </div>
              ) : (
                <div className="space-y-4">
                  {proximosAtendimentos.map((atendimento) => (
                    <div
                      key={atendimento.id}
                      className="flex items-center justify-between border-b pb-3 last:border-0"
                    >
                      <div>
                        <p className="font-medium text-foreground">{atendimento.pacienteNome}</p>
                        <p className="text-sm text-muted-foreground">
                          {atendimento.tipoAtendimento}
                        </p>
                      </div>
                      <span className="text-sm text-accent font-medium">
                        {formatarDataHora(atendimento.dataAgendamento, atendimento.horaInicio)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
